import React from 'react';
import { SavedIcon, Spinner } from 'evergreen-ui';
import AppButton from 'components/AppButton/AppButton';
import { isValidMeetingUrl } from 'utils/meetingDomains';
import { TabPanel } from '../components/TabPanel';
import { SwitchRow } from '../components/SwitchRow';
import { MultiSwitchRow } from '../components/MultiSwitchRow';
import { MeetingUrlsEditor } from '../components/MeetingUrlsEditor';
import styles from '../TargetGroupSettings.module.css';

interface MotiviewTabProps {
    tab: string;
    index: number;
    selectedIndex: number;

    allowMotiviewRegistration: boolean;
    setAllowMotiviewRegistration: (value: boolean) => void;
    isWebsiteDemo: boolean;
    setIsWebsiteDemo: (value: boolean) => void;
    allowRepeatMotiviewScan: boolean;
    setAllowRepeatMotiviewScan: (value: boolean) => void;

    showMotiviewDetailsFormBeforeResult: boolean;
    setShowMotiviewDetailsFormBeforeResult: (value: boolean) => void;
    showMotiviewDetailsFormAfterResult: boolean;
    setShowMotiviewDetailsFormAfterResult: (value: boolean) => void;

    showCompanyLogo: boolean;
    setShowCompanyLogo: (value: boolean) => void;

    sendPdfReport: boolean;
    setSendPdfReport: (value: boolean) => void;

    allowMeetingAboveAverage: boolean;
    setAllowMeetingAboveAverage: (value: boolean) => void;
    allowMeetingAverage: boolean;
    setAllowMeetingAverage: (value: boolean) => void;
    allowMeetingBelowAverage: boolean;
    setAllowMeetingBelowAverage: (value: boolean) => void;

    meetingUrls: string[];
    setMeetingUrls: (urls: string[]) => void;

    isLoading: boolean;
    displaySuccess: boolean;
    onSave: () => void;
}

/**
 * All Motiview settings, extracted from the Settings tab into their
 * own tab. The save handler is scoped to motiview fields only — saving here
 * cannot accidentally overwrite the General/Settings/Notifications fields.
 *
 * The two "Show candidate details form" toggles are mutually exclusive:
 * turning one ON forces the other OFF, and each is disabled while the other
 * is ON. This matches the original behavior.
 *
 * Meeting URLs row appears only when at least one meeting flag is enabled.
 * The Save button is disabled when meeting flags are on but no valid URL
 * has been entered.
 */
export const MotiviewTab = ({
    tab,
    index,
    selectedIndex,
    allowMotiviewRegistration,
    setAllowMotiviewRegistration,
    isWebsiteDemo,
    setIsWebsiteDemo,
    allowRepeatMotiviewScan,
    setAllowRepeatMotiviewScan,
    showMotiviewDetailsFormBeforeResult,
    setShowMotiviewDetailsFormBeforeResult,
    showMotiviewDetailsFormAfterResult,
    setShowMotiviewDetailsFormAfterResult,
    showCompanyLogo,
    setShowCompanyLogo,
    sendPdfReport,
    setSendPdfReport,
    allowMeetingAboveAverage,
    setAllowMeetingAboveAverage,
    allowMeetingAverage,
    setAllowMeetingAverage,
    allowMeetingBelowAverage,
    setAllowMeetingBelowAverage,
    meetingUrls,
    setMeetingUrls,
    isLoading,
    displaySuccess,
    onSave,
}: MotiviewTabProps) => {
    const anyMeetingFlagEnabled =
        allowMeetingAboveAverage ||
        allowMeetingAverage ||
        allowMeetingBelowAverage;
    const hasAtLeastOneValidUrl = meetingUrls.some((u) =>
        isValidMeetingUrl(u.trim()),
    );
    const meetingConfigValid = !anyMeetingFlagEnabled || hasAtLeastOneValidUrl;

    return (
        <TabPanel tab={tab} index={index} selectedIndex={selectedIndex}>
            <table>
                <colgroup>
                    <col className={styles.setting_key} />
                    <col className={styles.setting_value} />
                </colgroup>
                <tbody>
                    <SwitchRow
                        label="Allow Motiview Registration"
                        description="Enable this option to allow participants to register for Motiview assessments"
                        checked={allowMotiviewRegistration}
                        onChange={setAllowMotiviewRegistration}
                    />

                    <SwitchRow
                        label="Activate Website Version"
                        description="Enable this option if you want to use the website version of Motiview"
                        checked={isWebsiteDemo}
                        onChange={setIsWebsiteDemo}
                    />

                    <SwitchRow
                        label="Allow repeat scan"
                        description="Allow candidates to complete the Motiview scan multiple times from the same device"
                        checked={allowRepeatMotiviewScan}
                        onChange={setAllowRepeatMotiviewScan}
                    />

                    <MultiSwitchRow
                        label="Show candidate details form"
                        description="Collect the candidate's details via an intake form. Choose to display it before the slideshow during the analysis wait, or after the result on the way to scheduling. Only one option can be enabled at a time."
                        switches={[
                            {
                                label: 'Before result',
                                checked: showMotiviewDetailsFormBeforeResult,
                                // Mutually exclusive with "After result" — turning
                                // this on forces the other off, and each is
                                // disabled while the other is on.
                                onChange: (newVal) => {
                                    setShowMotiviewDetailsFormBeforeResult(
                                        newVal,
                                    );
                                    if (newVal)
                                        setShowMotiviewDetailsFormAfterResult(
                                            false,
                                        );
                                },
                                disabled: showMotiviewDetailsFormAfterResult,
                            },
                            {
                                label: 'After result',
                                checked: showMotiviewDetailsFormAfterResult,
                                onChange: (newVal) => {
                                    setShowMotiviewDetailsFormAfterResult(
                                        newVal,
                                    );
                                    if (newVal)
                                        setShowMotiviewDetailsFormBeforeResult(
                                            false,
                                        );
                                },
                                disabled: showMotiviewDetailsFormBeforeResult,
                            },
                        ]}
                    />

                    <SwitchRow
                        label="Show company logo"
                        description="Display the company logo instead of the Motiview logo"
                        checked={showCompanyLogo}
                        onChange={setShowCompanyLogo}
                    />

                    <SwitchRow
                        label="Allow candidates to email themselves the PDF report"
                        description="Show an email input on the Motiview result page. The candidate receives a signed link to their PDF report, valid for 14 days. Maximum 3 sends per candidate."
                        checked={sendPdfReport}
                        onChange={setSendPdfReport}
                    />

                    <MultiSwitchRow
                        label="Allow meetings"
                        description="Show meeting-scheduling URLs on the result page based on the candidate's culture-fit score. Each score level can be enabled independently. At least one Meeting URL must be configured for any enabled level to take effect."
                        switches={[
                            {
                                label: 'Above average',
                                checked: allowMeetingAboveAverage,
                                onChange: setAllowMeetingAboveAverage,
                            },
                            {
                                label: 'Average',
                                checked: allowMeetingAverage,
                                onChange: setAllowMeetingAverage,
                            },
                            {
                                label: 'Below average',
                                checked: allowMeetingBelowAverage,
                                onChange: setAllowMeetingBelowAverage,
                            },
                        ]}
                    />

                    {anyMeetingFlagEnabled && (
                        <tr>
                            <td colSpan={2}>
                                <MeetingUrlsEditor
                                    urls={meetingUrls}
                                    onChange={setMeetingUrls}
                                />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className={styles.submit_wrap}>
                <span className={styles.save_result}>
                    {isLoading ? (
                        <Spinner size={15} />
                    ) : displaySuccess ? (
                        'Saved!'
                    ) : (
                        ''
                    )}
                </span>
                <AppButton
                    icon={SavedIcon}
                    onClick={onSave}
                    width={'120px'}
                    isDisabled={isLoading || !meetingConfigValid}>
                    Save
                </AppButton>
            </div>
        </TabPanel>
    );
};

export default MotiviewTab;
